import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';

export default class extends Controller {
  static targets = [
    'sectionsList',
    'noSectionOption',
    'title',
    'nameInput',
    'nameError',
    'submitButton',
  ];
  static values = { url: String };
  #moving = false;

  refreshMenu() {
    const rows = this.#selectedRows();
    const currentSectionKey = this.#currentSectionKey(rows);

    this.titleTarget.textContent = `Move ${rows.length} ${rows.length === 1 ? 'item' : 'items'} to...`;
    const options = this.#sectionElements().map((section) =>
      this.#sectionOption(section, currentSectionKey)
    );
    this.sectionsListTarget.replaceChildren(...options);
    this.#toggleOptionDisabled(this.noSectionOptionTarget, currentSectionKey === 'unassigned');
    this.#resetNewSectionForm();
  }

  moveToSection(event) {
    this.#move({ sectionId: event.params.sectionId });
  }

  moveToNoSection() {
    this.#move({});
  }

  syncNewSectionState() {
    this.submitButtonTarget.disabled = this.nameInputTarget.value.trim() === '';
  }

  nameInputKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitNewSection();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.#resetNewSectionForm();
    }
  }

  commitNewSection() {
    const name = this.nameInputTarget.value.trim();

    if (name === '') {
      this.#toggleNameError(true);
      return;
    }

    this.#toggleNameError(false);
    this.#move({ name });
  }

  async #move({ sectionId = null, name = null }) {
    if (this.#moving) {
      return;
    }

    const rows = this.#selectedRows();
    if (rows.length === 0) {
      this.#closeMenu();
      return;
    }

    this.#moving = true;

    const body = new FormData();
    this.#itemIdsFor(rows).forEach((id) => body.append('item_ids[]', id));
    if (sectionId) {
      body.append('section_id', sectionId);
    }
    if (name) {
      body.append('name', name);
    }

    try {
      const request = new FetchRequest('post', this.urlValue, { body, responseKind: 'html' });
      const response = await request.perform();

      if (!response.ok) {
        if (name && response.unprocessableEntity) {
          this.#toggleNameError(true);
        } else {
          window.alert('The items could not be moved. Please try again.');
        }
        return;
      }

      const destination = name
        ? this.#insertNewSection(await response.html)
        : this.#destinationSection(sectionId);
      this.#relocateRows(rows, destination);
      this.#closeMenu();
    } catch (error) {
      console.error(error);
    } finally {
      this.#moving = false;
    }
  }

  #relocateRows(rows, destination) {
    const addItemBar = destination.querySelector(':scope > .billable-section__add-item');
    if (addItemBar) {
      addItemBar.before(...rows);
    } else {
      destination.append(...rows);
    }

    this.element.closest('.batch-action-bar')?.dispatchEvent(new Event('cancel'));

    // A freshly inserted section's Stimulus bindings connect asynchronously,
    // so events dispatched in the same tick as #insertNewSection are missed.
    setTimeout(() => {
      this.dispatch('expand', { prefix: 'billable-section', target: destination });
      this.dispatch('items-moved', { prefix: 'billable-section' });

      const lastRow = rows[rows.length - 1];
      const scrollAnchor = lastRow.offsetParent ? lastRow : destination;
      scrollAnchor.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  }

  #insertNewSection(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    const section = template.content.firstElementChild;

    const unassigned = this.#grid.querySelector('.billable-section--unassigned');
    if (unassigned) {
      unassigned.before(section);
    } else {
      this.#grid.append(section);
    }

    return section;
  }

  #destinationSection(sectionId) {
    if (sectionId) {
      return this.#grid.querySelector(`.billable-section[data-section-id='${sectionId}']`);
    }

    return this.#grid.querySelector('.billable-section--unassigned');
  }

  #sectionElements() {
    return [
      ...this.#grid.querySelectorAll(
        '.billable-section[data-section-id]:not(.billable-section--unassigned)'
      ),
    ];
  }

  #sectionOption(section, currentSectionKey) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dropdown-menu__link';
    button.textContent = section.dataset.billableSectionNameValue;
    button.dataset.action = 'billable-move-to-section#moveToSection';
    button.dataset.billableMoveToSectionSectionIdParam = section.dataset.sectionId;
    this.#toggleOptionDisabled(button, section.dataset.sectionId === currentSectionKey);
    return button;
  }

  // The destination the whole selection already lives in gets disabled;
  // null (nothing disabled) when the selection spans several sections.
  #currentSectionKey(rows) {
    const keys = new Set(
      rows.map((row) => {
        const section = row.closest('.billable-section');
        return section?.classList.contains('billable-section--unassigned')
          ? 'unassigned'
          : section?.dataset.sectionId;
      })
    );

    return keys.size === 1 ? [...keys][0] : null;
  }

  #toggleOptionDisabled(button, disabled) {
    button.disabled = disabled;
    button.classList.toggle('dropdown-menu__link--disabled', disabled);
  }

  #selectedRows() {
    const checkboxes = this.#form.querySelectorAll('.checkbox--group-billable-item:checked');
    const rows = new Set();

    checkboxes.forEach((checkbox) => {
      const item = checkbox.closest('.billable-item');
      rows.add(item.parentElement.closest('.billable-item') ?? item);
    });

    return [...rows];
  }

  #itemIdsFor(rows) {
    return rows.flatMap((row) =>
      [row, ...row.querySelectorAll('.billable-item')].map((el) => el.dataset.id)
    );
  }

  #closeMenu() {
    this.element.classList.remove('dropdown-menu--active');
    this.#resetNewSectionForm();
  }

  #resetNewSectionForm() {
    this.nameInputTarget.value = '';
    this.submitButtonTarget.disabled = true;
    this.#toggleNameError(false);
  }

  #toggleNameError(visible) {
    this.nameInputTarget.classList.toggle('billable-move-to-section__name-input--invalid', visible);
    this.nameErrorTarget.hidden = !visible;
  }

  get #form() {
    return this.element.closest('form');
  }

  get #grid() {
    return this.#form.querySelector('[data-billable-form-target~="itemsGrid"]');
  }
}
